<script lang="ts" setup>
	import { onClickOutside } from '@vueuse/core'
	import StringMethods from '~/utils/character'

	export type CustomSelectOptions = {
		label: unknown
		value: unknown
		disabled?: boolean
		checked?: boolean
	}
	const props = defineProps<{
		options: Array<CustomSelectOptions>

		id: unknown
		value: unknown[]
		label?: string
		labelClass?: ClassContext
		selectClass?: ClassContext
		required?: boolean
	}>()
	const emits = defineEmits<{
		(arg: 'change', param?: CustomSelectOptions): void
		(arg: 'update:value', param?: string[]): void
	}>()
	const dropdownIsActive = shallowRef(false)
	const { stripUnderScore } = StringMethods()
	const badgeWrapperElement = ref<HTMLElement>()
	const menuElement = ref<HTMLSelectElement>()
	const badgeElement = ref<HTMLElement>()
	const val = ref<unknown[]>([])
	const checkedOptions = computed(() => {
		return props.options.filter(item => item.checked).map(item => item.value)
	})
	const menuElementClientRect = computed(() => {
		const r = {
			width: 0,
			bottom: 0
		}
		if (menuElement.value?.children[0] && dropdownIsActive.value) {
			const inputWrapper = menuElement.value?.children[0]
			const { width, bottom } = inputWrapper.getBoundingClientRect()
			r.bottom = bottom
			r.width = width
		}
		return r
	})
	const removeVal = (i: number) => {
		if (Array.isArray(val.value)) {
			const optionItem = val.value[i]
			const item = props.options.find(option => option.value === optionItem || option.label === optionItem)
			if (item && item.disabled) {
				// Skip removing item that was disabled
				return null
			}

			if (item) {
				item.checked = false
				// Remove option
				val.value.splice(i, 1)
			}
			scrollBadge()
		}
	}
	const scrollBadge = () => {
		if (badgeElement.value && Array.isArray(badgeElement.value)) {
			const clientValues = badgeElement.value.reduce((acc, item) => {
				const { x } = item?.getBoundingClientRect() ?? {}
				return acc + Math.round(x)
			}, 0)

			;(badgeWrapperElement.value as HTMLElement).scrollTo({
				behavior: 'smooth',
				left: clientValues
			})
		}
	}

	const setValue = (item: CustomSelectOptions) => {
		if (item.disabled) {
			return null
		}

		if (Array.isArray(val.value)) {
			const itemAlreadyExist = val.value.includes(item.value)
			if (!itemAlreadyExist) {
				scrollBadge()
				item.checked = true
			} else {
				// Uncheck the options that was initially selected
				const itemIndex = val.value.findIndex(i => i === item.value)
				item.checked = false
				removeVal(itemIndex)
				scrollBadge()
			}
		}
	}
	const setDropdown = () => {
		dropdownIsActive.value = false
	}

	onActivated(() => {
		if (useRoute().params?.id) {
			window.addEventListener('click', setDropdown)
		}
	})

	onDeactivated(() => {
		window.removeEventListener('click', setDropdown)
	})

	onMount(() => {
		val.value = props.value
	})
	onClickOutside(menuElement, () => {
		dropdownIsActive.value = false
	})

	watch(dropdownIsActive, e => {
		const item = Array.isArray(val.value) ? val.value[val.value.length - 1] : val.value

		if (!e) {
			emits('change', item)
			emits('update:value', checkedOptions.value)
		}
	})
	watch(
		() => props.options,
		() => {
			val.value = checkedOptions.value
		},
		{
			deep: true
		}
	)
</script>

<template>
	<div ref="menuElement" class="relative">
		<div
			class="custom-input inline-flex w-full items-center justify-between"
			@click="() => (dropdownIsActive = true)"
		>
			<div
				ref="badgeWrapperElement"
				class="relative mr-1 flex items-center overflow-x-auto overflow-y-hidden !py-2 pr-5"
				tabindex="1"
				@click.stop="() => (dropdownIsActive = true)"
			>
				<template v-if="Array.isArray(val) && val.length > 0">
					<div
						v-for="(item, index) in val"
						:key="index"
						ref="badgeElement"
						class="tag"
						@click="removeVal(index)"
					>
						{{ stripUnderScore(item) }}
						<span class="font-bold text-white">x</span>
					</div>
				</template>

				<input
					v-if="Array.isArray(value)"
					:id="id"
					type="text"
					class="mt- min-h-full w-full flex-none py-3 outline-none"
					:value="value.join(',')"
					hidden
				/>
			</div>

			<button
				class="right-1 mr-3 bg-gray-25 p-2 outline-primary-2 transition"
				:class="{
					'rotate-180': dropdownIsActive
				}"
				type="button"
			>
				<lazy-tools-icon icon="caret-down" path-class="stroke-gray-400" dir="arrows" />
			</button>
		</div>

		<ul
			ref="dropdownElement"
			class="custom-transition-2 fixed mt-4 max-h-[400px] min-h-[100px] w-full space-y-1 overflow-hidden overflow-y-auto rounded-md bg-white shadow-[0_6px_15px_rgba(0,0,0,0.05)]"
			:style="{
				zIndex: dropdownIsActive ? 10 : -10,
				pointerEvents: (!dropdownIsActive && 'none') || 'all',
				top: menuElementClientRect.bottom + 'px',
				opacity: `${dropdownIsActive ? '1' : '0'}`,
				transform: `scale(${dropdownIsActive ? '1' : '0.5'})`,
				maxWidth: `${menuElementClientRect.width}px`
			}"
			@click.prevent
		>
			<li
				class="flex cursor-pointer items-center rounded-md px-5 capitalize transition hover:bg-gray-50"
				@click.stop.prevent
			>
				<a-form-item-rest>
					<slot name="menu-item" />
				</a-form-item-rest>
			</li>
			<li
				v-for="(item, index) in options"
				:key="index"
				class="group flex cursor-pointer items-center rounded-md px-5 py-2 capitalize transition hover:bg-gray-50"
				:class="{
					'bg-gray-50': item.checked || item.disabled
				}"
				@click.stop.prevent="setValue(item)"
			>
				<a-form-item-rest>
					<a-checkbox
						class="flex h-full w-full items-start checked:!bg-gray-250 disabled:!bg-gray-500"
						:checked="item.checked"
						:disabled="item.disabled"
					>
						<span
							class="flex pt-1 font-medium text-gray-400 transition group-hover:text-gray-600"
							:class="{
								'text-gray-600': val?.includes(String(item.value).toLowerCase())
							}"
						>
							{{ item.label }}
						</span>
					</a-checkbox>
				</a-form-item-rest>
			</li>
		</ul>
	</div>
</template>
